// src/ProtectedPages/ProtectedAuditLogs.js
import React, { useEffect, useRef, useState } from "react";
import {
    Box, Container, Typography, TextField, MenuItem, IconButton, Table, TableHead,
    TableRow, TableCell, TableBody, Collapse, Paper, TablePagination, Tooltip,
    CircularProgress, Chip
} from "@mui/material";
import { ExpandMore, ExpandLess, Refresh } from "@mui/icons-material";
import { motion } from "framer-motion";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { styled } from "@mui/material/styles";
import { authFetch } from '../Pages/Utils/authFetch'; // <<< AGGIUNTO
import { api } from '../config';
import DashboardBackButton from "../Components/Common/DashboardBackButton";

const API_URL = api("/audit-list.php");
const redGradient = "linear-gradient(90deg, #99191b 0%, #e3764c 100%)";

const ExpandButton = styled(IconButton)(({ theme, open }) => ({
    transform: open ? "rotate(180deg)" : "rotate(0deg)",
    transition: theme.transitions.create("transform", {
        duration: theme.transitions.duration.shortest,
    }),
}));

export default function ProtectedAuditLogs() {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [expandedRows, setExpandedRows] = useState({});
    const [projectsMap, setProjectsMap] = useState({});

    const username = localStorage.getItem('username') || '';
    const isAdmin = (() => {
        const u = String(username).toLowerCase();
        return u === 'admin' || u === 'giorgio';
    })();

    const [filters, setFilters] = useState({
        action: "", entity_table: "", entity_id: "", user_id: "", q: "",
    });
    const [userOptions, setUserOptions] = useState([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [totalRows, setTotalRows] = useState(0);

    const fetchLogs = async (offset = 0) => {
        try {
            setLoading(true);

            const params = new URLSearchParams({
                limit: String(rowsPerPage),
                offset: String(offset),
            });
            Object.entries(filters).forEach(([k, v]) => {
                if ((v || "").toString().trim() !== "") params.append(k, v.toString().trim());
            });

            const res = await authFetch(`${API_URL}?${params.toString()}`, { method: 'GET' });
            const data = await res.json();

            if (!data.success) throw new Error(data.error || "Errore API");

            setLogs(data.items || []);
            setTotalRows(typeof data.count === "number" ? data.count : (data.items || []).length);
        } catch (e) {
            console.error(e);
            toast.error(`Problema nel caricamento: ${e.message}`);
        } finally {
            setLoading(false);
        }
    };

    const fetchUserOptions = async () => {
        try {
            const res = await authFetch(`${API_URL}?limit=1&offset=0`, { method: 'GET' });
            const data = await res.json();
            if (!data.success) return;
            const items = Array.isArray(data.userOptions) ? data.userOptions : [];
            setUserOptions(items);
        } catch {
            // silenzioso: opzionale
        }
    };

    useEffect(() => {
        fetchLogs(page * rowsPerPage);
        if (isAdmin) fetchUserOptions();
        /* eslint-disable-next-line */
    }, [page, rowsPerPage]);

    useEffect(() => {
        fetch(api("/getProjects.php"))
            .then(r => r.json())
            .then(data => {
                const map = {};
                (data.projects || data || []).forEach(p => {
                    map[p.id] = { titolo: p.Titolo || p.titolo || '—', firstImage: (p.images && p.images[0]) ? (p.images[0].path || p.images[0].image_path) : null };
                });
                setProjectsMap(map);
            })
            .catch(() => {});
        /* eslint-disable-next-line */
    }, []);

    const handleSearch = () => { setPage(0); fetchLogs(0); };

    const isMounted = useRef(false);
    useEffect(() => {
        if (!isMounted.current) { isMounted.current = true; return; }
        const timer = setTimeout(() => { setPage(0); fetchLogs(0); }, 350);
        return () => clearTimeout(timer);
        // eslint-disable-next-line
    }, [filters]);
    const toggleExpand = (id) => setExpandedRows((p) => ({ ...p, [id]: !p[id] }));
    const handleChangePage = (_e, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); };

    const getProjectInfo = (log) => {
        if (log.entity_table !== 'progetti') return null;
        try {
            const d = typeof log.details === 'string' ? JSON.parse(log.details) : log.details;
            if (!d) return null;
            const record = d.after?.record || d.before?.record || d.record || {};
            const images = d.after?.images || d.before?.images || [];
            return {
                title: record.Titolo || record.titolo || null,
                firstImage: images.length > 0 ? images[0].image_path : null,
            };
        } catch { return null; }
    };

    const PAGE_SIZE = 14;
    const ProjectCardGrid = ({ ids, getTitolo }) => {
        const [page, setPage] = React.useState(0);
        const totalPages = Math.ceil(ids.length / PAGE_SIZE);
        const visible = ids.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);
        return (
            <Box>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: totalPages > 1 ? 1 : 0 }}>
                    {visible.map((id, i) => <ProjectCard key={id} id={id} index={page * PAGE_SIZE + i} titolo={getTitolo ? getTitolo(id) : null} />)}
                </Box>
                {totalPages > 1 && (
                    <Box sx={{ display: "flex", gap: 0.5, mt: 1 }}>
                        {Array.from({ length: totalPages }, (_, i) => (
                            <Box key={i} onClick={() => setPage(i)} sx={{ cursor: "pointer", px: 1.2, py: 0.3, borderRadius: 1, fontSize: 12, fontWeight: page === i ? 700 : 400, backgroundColor: page === i ? "#99191b" : "#eee", color: page === i ? "#fff" : "#333" }}>
                                {i + 1}
                            </Box>
                        ))}
                    </Box>
                )}
            </Box>
        );
    };

    const ProjectCard = ({ id, index, titolo }) => {
        const p = projectsMap[id];
        const imgSrc = p?.firstImage ? api(`/Progetti/${id}/${p.firstImage}`) : null;
        const label = titolo || p?.titolo || `#${id}`;
        return (
            <Box sx={{ position: "relative", width: 130, height: 90, borderRadius: 2, overflow: "hidden", border: "1px solid #ddd", backgroundColor: "#ccc", flexShrink: 0 }}>
                {imgSrc && <Box component="img" src={imgSrc} sx={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} onError={(e) => { e.target.style.display = "none"; }} />}
                <Box sx={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.7) 100%)" }} />
                {index != null && (
                    <Box sx={{ position: "absolute", top: 5, left: 6, backgroundColor: "#99191b", color: "#fff", fontSize: 9, fontWeight: 700, borderRadius: "50%", width: 16, height: 16, display: "flex", alignItems: "center", justifyContent: "center" }}>
                        {index + 1}
                    </Box>
                )}
                <Typography sx={{ position: "absolute", bottom: 5, left: 6, right: 6, fontSize: 10, fontWeight: 600, color: "#fff", lineHeight: 1.3, wordBreak: "break-word" }}>{label}</Typography>
            </Box>
        );
    };

    const InfoRow = ({ label, value }) => value != null && String(value).trim() ? (
        <Box sx={{ display: "contents" }}>
            <Typography variant="caption" sx={{ fontWeight: 600, color: "#666" }}>{label}</Typography>
            <Typography variant="caption" sx={{ color: "#333" }}>{value}</Typography>
        </Box>
    ) : null;

    const renderDetails = (details, log) => {
        if (!details) return <Typography variant="body2" color="text.secondary">Nessun dettaglio.</Typography>;
        let d;
        try { d = typeof details === "string" ? JSON.parse(details) : details; }
        catch { return <Typography variant="body2" color="text.secondary">Dati non disponibili.</Typography>; }

        // ── login ────────────────────────────────────────────────────
        if (String(log?.action).toLowerCase() === "login") {
            return (
                <Box sx={{ display:"flex", alignItems:"center", gap:2 }}>
                    <Box sx={{
                        width:42, height:42, borderRadius:"50%",
                        background: redGradient,
                        display:"flex", alignItems:"center", justifyContent:"center",
                        color:"#fff", fontSize:22, flexShrink:0,
                    }}>👤</Box>
                    <Box>
                        <Typography variant="body2" sx={{ fontWeight:700, fontFamily:"'Poppins',sans-serif" }}>Accesso effettuato</Typography>
                        <Typography variant="caption" color="text.secondary">Utente: <strong>{d.username || log.username || "—"}</strong></Typography>
                    </Box>
                </Box>
            );
        }

        // ── finanza_selections ──────────────────────────────────────
        if (log?.entity_table === "finanza_selections") {
            const newFmt = d.progetti_selezionati || [];
            const ids = newFmt.length > 0 ? newFmt.map(p => p.id) : (d.selected_project_ids || []);
            return (
                <Box>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: "#99191b", display: "block", mb: 1 }}>Progetti selezionati ({ids.length})</Typography>
                    <ProjectCardGrid ids={ids} getTitolo={id => (newFmt.find(x => x.id === id) || {}).titolo} />
                </Box>
            );
        }

        // ── project_order (reorder) ─────────────────────────────────
        if (d.order && Array.isArray(d.order)) {
            return (
                <Box>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: "#99191b", display: "block", mb: 1 }}>Nuovo ordine ({d.order.length} progetti)</Typography>
                    <ProjectCardGrid ids={d.order} />
                </Box>
            );
        }

        // ── progetti update (before/after) ──────────────────────────
        if (d.before && d.after) {
            const before = d.before.record || {};
            const after = d.after.record || {};
            const fields = [
                { key: "Titolo", label: "Titolo" }, { key: "Committente", label: "Committente" },
                { key: "Luogo", label: "Luogo" }, { key: "Incarico", label: "Incarico" },
                { key: "DataInizio", label: "Inizio" }, { key: "DataFine", label: "Fine" },
            ];
            const changed = fields.filter(f => String(before[f.key] ?? "") !== String(after[f.key] ?? ""));
            const bi = (d.before.images || []).length, ai = (d.after.images || []).length;
            return (
                <Box>
                    {changed.length === 0 ? (
                        <Typography variant="caption" sx={{ color:"#888", fontStyle:"italic", display:"block", mb:1 }}>Nessuna modifica ai campi (apertura senza salvataggio)</Typography>
                    ) : (
                        <Typography variant="caption" sx={{ fontWeight:700, color:"#99191b", display:"block", mb:1 }}>{changed.length} campo/i modificato/i</Typography>
                    )}
                    {changed.map(f => (
                        <Box key={f.key} sx={{ mb: 0.8 }}>
                            <Typography variant="caption" sx={{ fontWeight: 600, color: "#555", display: "block" }}>{f.label}</Typography>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mt: 0.2 }}>
                                <Chip label={before[f.key] || "—"} size="small" sx={{ backgroundColor: "#fce4e4", color: "#b71c1c", maxWidth: 300 }} />
                                <Typography variant="caption" sx={{ color: "#999" }}>→</Typography>
                                <Chip label={after[f.key] || "—"} size="small" sx={{ backgroundColor: "#e8f5e9", color: "#1b5e20", maxWidth: 300 }} />
                            </Box>
                        </Box>
                    ))}
                    {bi !== ai && <Typography variant="caption" sx={{ display: "block", mt: 0.5, color: "#777" }}>Immagini: {bi} → {ai}</Typography>}
                </Box>
            );
        }

        // ── progetti delete ─────────────────────────────────────────
        if (d.record && log?.action === "delete") {
            const r = d.record;
            const imgs = d.images || [];
            return (
                <Box>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: "#b71c1c", display: "block", mb: 1 }}>Progetto eliminato</Typography>
                    <Box sx={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: "3px 12px", mb: 1 }}>
                        <InfoRow label="Titolo" value={r.Titolo} />
                        <InfoRow label="Committente" value={r.Committente} />
                        <InfoRow label="Luogo" value={r.Luogo} />
                        <InfoRow label="Incarico" value={r.Incarico} />
                        <InfoRow label="Periodo" value={r.DataInizio && r.DataFine ? `${r.DataInizio} – ${r.DataFine}` : null} />
                    </Box>
                    {imgs.length > 0 && <Typography variant="caption" sx={{ color: "#777" }}>{imgs.length} immagine/i rimosse</Typography>}
                </Box>
            );
        }

        // ── progetti create ─────────────────────────────────────────
        if (d.new) {
            const r = d.new;
            return (
                <Box>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: "#1b5e20", display: "block", mb: 1 }}>Progetto creato</Typography>
                    <Box sx={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: "3px 12px" }}>
                        <InfoRow label="Titolo" value={r.Titolo || r.titolo} />
                        <InfoRow label="Committente" value={r.Committente || r.committente} />
                        <InfoRow label="Luogo" value={r.Luogo || r.citta} />
                        <InfoRow label="Incarico" value={r.Incarico || r.incarico} />
                        <InfoRow label="Periodo" value={r.DataInizio && r.DataFine ? `${r.DataInizio} – ${r.DataFine}` : null} />
                        <InfoRow label="Immagini" value={d.images?.count != null ? `${d.images.count} immagine/i` : null} />
                    </Box>
                </Box>
            );
        }

        // ── fallback: tabella chiave-valore ─────────────────────────
        const entries = Object.entries(d).filter(([, v]) => typeof v !== "object" || v === null);
        return (
            <Box sx={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: "3px 12px" }}>
                {entries.map(([k, v]) => <InfoRow key={k} label={k} value={String(v ?? "—")} />)}
            </Box>
        );
    };

    return (
        <>
            <ToastContainer position="top-right" autoClose={3000} theme="light" />

            <Box sx={{ width:"100%", minHeight:{ xs:180, sm:200 }, display:"flex", alignItems:"center", justifyContent:"center", background:redGradient, color:"#fff", borderBottom:"2px solid #fff", position:"relative" }}>
                <DashboardBackButton
                    boxSx={{ position: 'absolute', left: { xs: 10, sm: 18 }, top: '50%', transform: 'translateY(-50%)', p: 0 }}
                    buttonSx={{
                        color: '#fff',
                        backgroundColor: 'rgba(255,255,255,0.12)',
                        border: '1px solid rgba(255,255,255,0.26)',
                        '&:hover': { backgroundColor: 'rgba(255,255,255,0.18)' },
                    }}
                />

                <Typography variant="h3" sx={{ fontFamily:"'Poppins', sans-serif", fontWeight:700, letterSpacing:.5, px: { xs: 6, sm: 0 }, fontSize: { xs: "2rem", sm: "clamp(2.5rem, 5vw, 3.5rem)" } }}>
                    REGISTRO ATTIVITÀ
                </Typography>
            </Box>

            <Container sx={{ mt:4, maxWidth:"1200px !important" }}>
                <Paper sx={{ p:2, mb:2, borderRadius:2 }}>
                    <Typography variant="h6" sx={{ mb:1, fontFamily:"'Poppins', sans-serif", fontWeight:600, color:"#99191b" }}>Filtri</Typography>
                    <Box sx={{ display:"grid", gap:2, gridTemplateColumns:{ xs:"1fr", sm:"repeat(3,1fr)", md:"repeat(5,1fr)" }}}>
                        <TextField select label="Operazione" value={filters.action} onChange={(e)=>setFilters({ ...filters, action:e.target.value })} size="small">
                            <MenuItem value="">Tutte</MenuItem>
                            <MenuItem value="update">Modifica</MenuItem>
                            <MenuItem value="create">Creazione</MenuItem>
                            <MenuItem value="delete">Eliminazione</MenuItem>
                            <MenuItem value="reorder">Riordino</MenuItem>
                        </TextField>
                        <TextField select label="Tabella" value={filters.entity_table} onChange={(e)=>setFilters({ ...filters, entity_table:e.target.value })} size="small">
                            <MenuItem value="">Tutte</MenuItem>
                            <MenuItem value="progetti">Progetti</MenuItem>
                            <MenuItem value="finanza_selections">Finanza di Progetto</MenuItem>
                            <MenuItem value="project_order_global">Ordinamento</MenuItem>
                        </TextField>
                        <TextField label="ID elemento" value={filters.entity_id} onChange={(e)=>setFilters({ ...filters, entity_id:e.target.value })} size="small" />
                        {isAdmin ? (
                            <TextField
                                select
                                label="Utente"
                                value={filters.user_id}
                                onChange={(e) => setFilters({ ...filters, user_id: e.target.value })}
                                size="small"
                            >
                                <MenuItem value="">Tutti</MenuItem>
                                {userOptions.map((u) => (
                                    <MenuItem key={u.id} value={u.id}>
                                        {u.username}
                                    </MenuItem>
                                ))}
                            </TextField>
                        ) : (
                            <TextField
                                label="Utente"
                                value={username}
                                size="small"
                                disabled
                                helperText=""
                            />
                        )}
                        <TextField label="Testo" value={filters.q} onChange={(e)=>setFilters({ ...filters, q:e.target.value })} size="small" />
                    </Box>
                    <Box sx={{ mt:1, display:"flex", gap:2, justifyContent:"flex-end" }}>
                        <Tooltip title="Aggiorna"><IconButton color="primary" onClick={()=>fetchLogs(page*rowsPerPage)}><Refresh/></IconButton></Tooltip>
                    </Box>
                </Paper>

                <Paper sx={{ overflow:"hidden", borderRadius:2 }}>
                    {loading ? (
                        <Box sx={{ py:6, textAlign:"center" }}><CircularProgress color="error" /></Box>
                    ) : logs.length === 0 ? (
                        <Typography sx={{ p:4, textAlign:"center", opacity:.7 }}>Nessun log trovato</Typography>
                    ) : (
                        <>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>ID</TableCell>
                                        <TableCell>Utente</TableCell>
                                        <TableCell>Operazione</TableCell>
                                        <TableCell>Elemento</TableCell>
                                        <TableCell>Dove</TableCell>
                                        <TableCell>Quando</TableCell>
                                        <TableCell align="center">Dettagli</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {logs.map((log) => (
                                        <React.Fragment key={log.id}>
                                            <TableRow
                                                component={motion.tr}
                                                initial={{ opacity: 0 }}
                                                animate={{ opacity: 1 }}
                                                whileHover={{ backgroundColor: "rgba(0,0,0,0.03)" }}
                                            >
                                                <TableCell sx={{ fontSize: 13 }}>{log.id}</TableCell>
                                                <TableCell sx={{ fontSize: 13 }}>
                                                    <Box sx={{ display:"flex", alignItems:"center", gap:1, flexWrap:"wrap" }}>
                                                        <Box sx={{
                                                            px:1.2, py:0.4, borderRadius:999,
                                                            fontSize:12, fontWeight:700,
                                                            color: (() => {
                                                                const u = String(log.username || '').toLowerCase();
                                                                if (u === 'admin') return '#b71c1c';
                                                                if (u === 'editor') return '#1b5e20';
                                                                if (u === 'giorgio') return '#8a6d00';
                                                                return '#333';
                                                            })(),
                                                            backgroundColor: (() => {
                                                                const u = String(log.username || '').toLowerCase();
                                                                if (u === 'admin') return 'rgba(183,28,28,0.12)';
                                                                if (u === 'editor') return 'rgba(27,94,32,0.12)';
                                                                if (u === 'giorgio') return 'rgba(255,193,7,0.22)';
                                                                return 'rgba(0,0,0,0.06)';
                                                            })(),
                                                        }}>
                                                            {log.username || 'Utente'}
                                                        </Box>
                                                        <Typography variant="caption" color="text.secondary">#{log.user_id || '—'}</Typography>
                                                    </Box>
                                                </TableCell>
                                                <TableCell sx={{ fontWeight: 700, fontSize: 13 }}>{log.action}</TableCell>
                                                <TableCell sx={{ fontSize: 13 }}>
                                                    {(() => {
                                                        const info = getProjectInfo(log);
                                                        if (info) return (
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                {info.firstImage && (
                                                                    <Box component="img"
                                                                        src={api(`/Progetti/${log.entity_id}/${info.firstImage}`)}
                                                                        sx={{ width: 44, height: 44, objectFit: 'cover', borderRadius: 1, flexShrink: 0 }}
                                                                        onError={(e) => { e.target.style.display = 'none'; }}
                                                                    />
                                                                )}
                                                                <Box>
                                                                    <Typography sx={{ fontSize: 13, fontWeight: 600, lineHeight: 1.2 }}>{info.title || '—'}</Typography>
                                                                    <Typography variant="caption" color="text.secondary">ID {log.entity_id}</Typography>
                                                                </Box>
                                                            </Box>
                                                        );
                                                        return (<>{log.entity_table}<br /><Typography variant="caption" color="text.secondary">{log.entity_id}</Typography></>);
                                                    })()}
                                                </TableCell>
                                                <TableCell sx={{ fontSize: 13 }}>
                                                    <Typography>{log.page || "—"}</Typography>
                                                    <Typography variant="caption" color="text.secondary">{log.component || ""}</Typography>
                                                </TableCell>
                                                <TableCell sx={{ fontSize: 13 }}>
                                                    {log.created_at ? new Date(String(log.created_at).replace(" ", "T")).toLocaleString() : "—"}
                                                </TableCell>
                                                <TableCell align="center">
                                                    <ExpandButton
                                                        open={!!expandedRows[log.id]}
                                                        onClick={() => toggleExpand(log.id)}
                                                        color="error"
                                                    >
                                                        {expandedRows[log.id] ? <ExpandLess /> : <ExpandMore />}
                                                    </ExpandButton>
                                                </TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
                                                    <Collapse in={!!expandedRows[log.id]} timeout="auto" unmountOnExit>
                                                        <Box sx={{
                                                            m:1, p:2, backgroundColor:"#fafafa", borderRadius:2,
                                                            fontSize:13, overflowX:"auto",
                                                        }}>
                                                            {renderDetails(log.details, log)}
                                                            <Typography variant="caption" sx={{ color:"#777", mt:1, display:"block" }}>
                                                                Indirizzo: {log.ip || "—"} • Metodo: {log.method || "—"}
                                                            </Typography>
                                                        </Box>
                                                    </Collapse>
                                                </TableCell>
                                            </TableRow>
                                        </React.Fragment>
                                    ))}
                                </TableBody>
                            </Table>

                            <TablePagination
                                component="div"
                                count={totalRows}
                                page={page}
                                onPageChange={(_e, newPage) => setPage(newPage)}
                                rowsPerPage={rowsPerPage}
                                onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                                rowsPerPageOptions={[10, 25, 50, 100]}
                            />
                        </>
                    )}
                </Paper>
            </Container>
        </>
    );
}
